import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

export interface ConfirmDialogOptions {
  title?: string;
  message?: string;
  leftButton?: string;
  rightButton?: string;
  discourageConfirmation?: boolean;
  width?: string;
  height?: string;
  isDangerous?: boolean; // When true, the right button denies confirmation and close the dialog

  showTextarea?: boolean;
  placeholder?: string;
  helperText?: string;
}

@Component({
  standalone: true,
  selector: 'remote-update-domain-confirm-dialog',
  imports: [
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatButtonModule,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule
  ],
  templateUrl: './confirm-dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './confirm-dialog.scss'
})
export class ConfirmDialog {
  dialogRef = inject(MatDialogRef<ConfirmDialog>);
  data = inject(MAT_DIALOG_DATA) as ConfirmDialogOptions;

  submitAttempted = signal(false);

  hasReason = signal(false);

  rejectionRemarksForm = new FormGroup({
    remarks: new FormControl<string>('', Validators.required)
  });

  remarksControl = this.rejectionRemarksForm.controls.remarks;
  remarksValueSignal = toSignal(this.remarksControl.valueChanges, { initialValue: '' });

  constructor() {
    effect(() => {
      const value = this.remarksValueSignal();
      this.hasReason.set(!!value?.trim());
    });
  }

  onCancel() {
    this.dialogRef.close(this.data.discourageConfirmation ? true : false);
  }

  onConfirm() {
    if (this.data.showTextarea) {
      this.submitAttempted.set(true);

      if (this.rejectionRemarksForm.invalid) {
        this.remarksControl.markAsTouched();
        return; // Keep dialog open
      }

      const reason = this.remarksControl.value ?? '';
      this.dialogRef.close({ confirmed: true, reason });
      return;
    }

    this.dialogRef.close(this.data.discourageConfirmation ? false : true);
  }
}
